import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from '@/components/brand/Logo';
import { GlowButton } from '@/components/brand/GlowButton';
import { cn } from '@/lib/utils';

const LINKS = [
    { to: '/', label: 'Home' },
    { to: '/#services', label: 'Services' },
    { to: '/payments', label: 'Payments' },
    { to: '/contact', label: 'Contact' },
];

// --- Hooks -------------------------------------------------------------------

function useScrollState(threshold = 12) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > threshold);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [threshold]);
    return scrolled;
}

function useAnchorScrollHandler() {
    const location = useLocation();
    const navigate = useNavigate();
    return useCallback((e, to) => {
        if (!to.startsWith('/#')) return;
        const id = to.slice(2);
        e.preventDefault();
        const scrollToId = () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (location.pathname === '/') {
            scrollToId();
        } else {
            navigate('/');
            setTimeout(scrollToId, 140);
        }
    }, [location.pathname, navigate]);
}

// --- Sub-components ----------------------------------------------------------

const DesktopNav = ({ onAnchor }) => (
    <div className="hidden md:flex items-center">
        <nav className="flex items-center gap-10">
            {LINKS.map((l) => (
                <a
                    key={l.to}
                    href={l.to}
                    onClick={(e) => onAnchor(e, l.to)}
                    className="underline-grow text-[12.5px] tracking-[0.10em] uppercase text-la-text2 hover:text-la-text transition-colors duration-200"
                    data-testid={`nav-link-${l.label.toLowerCase()}`}
                >
                    {l.label}
                </a>
            ))}
        </nav>
        <span aria-hidden className="mx-8 h-6 w-px bg-white/12" />
        <GlowButton
            as={Link}
            to="/contact"
            size="sm"
            variant="gold"
            data-testid="navbar-book-consultation-button"
        >
            Book Consultation
        </GlowButton>
    </div>
);

const MobileNavToggle = ({ open, onToggle }) => (
    <button
        className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-la-sm border border-white/10 text-la-text"
        onClick={onToggle}
        data-testid="navbar-mobile-menu-button" aria-label="Toggle menu"
    >
        {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
    </button>
);

const MobileNavDrawer = ({ open, onAnchor }) => (
    <div className={cn(
        'md:hidden overflow-hidden transition-[max-height] duration-300 bg-la-canvas/96 backdrop-blur-md border-b border-white/8',
        open ? 'max-h-[420px]' : 'max-h-0',
    )}>
        <div className="px-5 py-5 flex flex-col gap-3">
            {LINKS.map((l) => (
                <a key={l.to} href={l.to} onClick={(e) => onAnchor(e, l.to)}
                    className="text-[13px] tracking-[0.08em] uppercase text-la-text2 hover:text-la-text py-1.5"
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}>
                    {l.label}
                </a>
            ))}
            <GlowButton as={Link} to="/contact" size="sm" variant="gold" className="w-fit mt-3">
                Book Consultation
            </GlowButton>
        </div>
    </div>
);

// --- Main Navbar -------------------------------------------------------------

export const Navbar = () => {
    const scrolled = useScrollState();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const handleAnchor = useAnchorScrollHandler();

    // Close the mobile drawer on route / anchor change.
    useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);

    return (
        <header
            data-testid="site-navbar"
            className={cn(
                'fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300',
                scrolled ? 'bg-la-canvas/92 backdrop-blur-md border-b border-white/8' : 'bg-transparent border-b border-transparent',
            )}
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-[76px]">
                <Logo size={44} />
                <DesktopNav onAnchor={handleAnchor} />
                <MobileNavToggle open={open} onToggle={() => setOpen((v) => !v)} />
            </div>
            <MobileNavDrawer open={open} onAnchor={handleAnchor} />
        </header>
    );
};

export default Navbar;
